import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/Badges";
import { Car, FileText, Save, Mail, StickyNote } from "lucide-react";
import { toast } from "sonner";
import NotesPanel from "@/components/NotesPanel";
import SendEmailDialog from "@/components/SendEmailDialog";

const FIELDS = [
  ["brand", "vehicles.brand"], ["model", "vehicles.model"], ["license_plate", "vehicles.plate"],
  ["vin", "vehicles.vin"], ["mileage", "vehicles.mileage"], ["first_registration", "vehicles.first_registration"],
  ["fuel_type", "vehicles.fuel"], ["engine", "vehicles.engine"],
];

export default function VehicleDetailDialog({ open, onOpenChange, vehicleId, canEdit, onSaved }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    if (!open || !vehicleId) return;
    (async () => {
      const [v, cs] = await Promise.all([
        api.get(`/vehicles/${vehicleId}`), api.get("/customers"),
      ]);
      setData(v.data); setCustomers(cs.data);
      setForm({
        customer_id: v.data.vehicle.customer_id || "",
        brand: v.data.vehicle.brand || "", model: v.data.vehicle.model || "",
        license_plate: v.data.vehicle.license_plate || "", vin: v.data.vehicle.vin || "",
        mileage: v.data.vehicle.mileage || 0,
        first_registration: v.data.vehicle.first_registration || "",
        fuel_type: v.data.vehicle.fuel_type || "", engine: v.data.vehicle.engine || "",
        notes: v.data.vehicle.notes || "",
        next_hu_date: v.data.vehicle.next_hu_date || "",
      });
    })();
  }, [open, vehicleId]);

  const save = async () => {
    try {
      await api.patch(`/vehicles/${vehicleId}`, {
        ...form, mileage: parseInt(form.mileage || 0, 10),
      });
      toast.success(t("common.save"));
      onSaved && onSaved();
      const { data } = await api.get(`/vehicles/${vehicleId}`);
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (!data || !form) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#21252E] border-[#2E3241] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Car className="h-5 w-5 text-blue-400" />
            {data.vehicle.brand} {data.vehicle.model} · {data.vehicle.license_plate}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="info" className="space-y-3">
          <TabsList className="bg-[#171B24] border border-[#2E3241]">
            <TabsTrigger value="info">{t("common.name")}</TabsTrigger>
            <TabsTrigger value="contracts" data-testid="veh-tab-contracts">
              {t("customers.linked_contracts")} ({data.contracts.length})
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="veh-tab-notes">
              <StickyNote className="h-3.5 w-3.5 mr-1" />{t("notes.title")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="surface rounded-lg p-4 space-y-3">
            <div>
              <Label className="text-xs">{t("contracts.customer")}</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger className="bg-[#171B24] border-[#2E3241]" disabled={!canEdit} data-testid="veh-detail-customer">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs">{t(l)}</Label>
                  <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    disabled={!canEdit} className="bg-[#171B24] border-[#2E3241]"
                    data-testid={`veh-detail-${k}`} />
                </div>
              ))}
              <div>
                <Label className="text-xs">{t("vehicle_extra.next_hu_date")}</Label>
                <Input
                  type="date"
                  value={form.next_hu_date || ""}
                  onChange={(e) => setForm({ ...form, next_hu_date: e.target.value })}
                  disabled={!canEdit}
                  className="bg-[#171B24] border-[#2E3241]"
                  data-testid="veh-detail-next_hu_date"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">{t("common.notes")}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                disabled={!canEdit} rows={3} className="bg-[#171B24] border-[#2E3241]" data-testid="veh-detail-notes" />
            </div>
          </TabsContent>

          <TabsContent value="contracts" className="space-y-2">
            {data.contracts.length === 0 && <div className="text-sm text-zinc-500">{t("common.empty")}</div>}
            {data.contracts.map((c) => (
              <div key={c.id}
                onClick={() => { onOpenChange(false); nav(`/contracts/${c.id}`); }}
                className="surface rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-[#2A2F3A]"
                data-testid={`veh-contract-${c.number}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-4 w-4 text-blue-400" />
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-sm text-blue-300">{c.number}</div>
                    <div className="text-xs text-zinc-400 truncate">
                      {c.title || "—"} · {c.planned_date || ""} · {c.mileage_at_service || ""}
                    </div>
                  </div>
                </div>
                <StatusBadge status={c.status} />
              </div>
            ))}
          </TabsContent>

          <TabsContent value="notes" className="surface rounded-lg p-4">
            <NotesPanel entityType="vehicle" entityId={vehicleId} />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          {canEdit && data.customer?.email && (
            <Button
              variant="outline"
              onClick={() => setEmailOpen(true)}
              className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
              data-testid="veh-email-btn"
            >
              <Mail className="h-4 w-4 mr-1" /> {t("emails.send")}
            </Button>
          )}
          {canEdit && (
            <Button onClick={save} className="bg-blue-500 hover:bg-blue-600" data-testid="veh-detail-save">
              <Save className="h-4 w-4 mr-1" /> {t("common.save")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      <SendEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        customerId={data.customer?.id}
        customerName={data.customer?.name}
        customerEmail={data.customer?.email}
        vehicleId={vehicleId}
      />
    </Dialog>
  );
}
